import { createPromise } from './create-promise'

type AsyncFn = AnyFn<Promise<any>>

export interface MergeAsyncFnOptions<T extends AsyncFn = AsyncFn> {
  time?: number
  argsResolver: (items: TaskItem<T>[]) => Parameters<T>
  resultResolver: (
    result: Awaited<ReturnType<T>>,
    item: TaskItem<T>,
    index: number,
    items: TaskItem<T>[],
  ) => Awaited<ReturnType<T>>
}

interface TaskItem<T extends AsyncFn = AsyncFn> {
  args: Parameters<T>
  invokeTime: number
  promise: ReturnType<typeof createPromise<Awaited<ReturnType<T>>>>
}

interface Task<T extends AsyncFn = AsyncFn> {
  items: TaskItem<T>[]
  firstInvokeTime: number
}

const taskMap = new WeakMap<AsyncFn, Task>()

export function mergeAsyncFn<T extends AsyncFn>(
  fn: T,
  { time = 32, argsResolver, resultResolver }: MergeAsyncFnOptions<T>,
): T {
  function mergedAsyncFn(...args: Parameters<T>) {
    const now = Date.now()

    let task = taskMap.get(fn)

    if (!task) {
      taskMap.set(
        fn,
        (task = {
          items: [],
          firstInvokeTime: now,
        }),
      )
    }

    const p = createPromise()

    task.items.push({
      args,
      invokeTime: now,
      promise: p,
    })

    if (task.items.length === 1) {
      runTask()
    }

    return p
  }

  function runTask() {
    setTimeout(() => {
      const task = taskMap.get(fn) as null | Task<T>
      if (!task) return
      taskMap.delete(fn)

      const mergedArgs = argsResolver(task.items)

      fn(...mergedArgs)
        .then((result) => {
          task.items.forEach((item, index) => {
            item.promise.resolve(
              resultResolver(result, item, index, task.items),
            )
          })
        })
        .catch((error) => {
          task.items.forEach((item) => {
            item.promise.reject(error)
          })
        })
    }, time)
  }

  return mergedAsyncFn as unknown as T
}
